import React, { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { getConditionOptions } from "../workflow.types";
import type { ConditionFieldDef, NodeDefArgField, NodeRunState, OutputPort, WorkflowNode } from "../workflow.types";
import { conditionColorSuffix } from "./canvasGeometry";
import type { ResizeDirection } from "./canvasGeometry";

/** Top/bottom edges + all 4 corners never sit under a port, so one handle
 * each is enough. The east/west (left/right) edges are handled separately,
 * below, split in two around the vertical center — that's exactly where the
 * "in"/"out" port dot sits (`.wf-port` z-index: 2 beats an unsplit strip
 * there), so grabbing the middle of the edge would wire a connection instead
 * of resizing. */
const RESIZE_DIRECTIONS: ResizeDirection[] = ["n", "s", "ne", "nw", "se", "sw"];

interface NodeCardProps {
  node: WorkflowNode;
  label: string;
  description?: string;
  argFields?: NodeDefArgField[];
  conditionFields: ConditionFieldDef[];
  runState: NodeRunState;
  selected: boolean;
  isInputHighlighted: boolean;
  onHeaderMouseDown: (e: React.MouseEvent) => void;
  /** Double-click (or drag) — applies the canvas "selected" highlight. */
  onSelect: (e: React.MouseEvent) => void;
  /** Single click — opens the properties panel without highlighting the node. */
  onOpenPanel: (e: React.MouseEvent) => void;
  onPortMouseDown: (port: OutputPort, e: React.MouseEvent) => void;
  onRename: (label: string) => void;
  onDuplicate: () => void;
  onResizeMouseDown: (direction: ResizeDirection, e: React.MouseEvent) => void;
  registerRef: (el: HTMLDivElement | null) => void;
}

const CATEGORY_CLASS: Record<WorkflowNode["type"], string> = {
  trigger: "wf-node--trigger",
  action: "wf-node--action",
  condition: "wf-node--condition",
};

function CategoryIcon({ type }: { type: WorkflowNode["type"] }) {
  if (type === "trigger") return <BoltIcon fontSize="small" />;
  if (type === "condition") return <CallSplitIcon fontSize="small" />;
  return <SmartToyIcon fontSize="small" />;
}

function RunBadge({ state }: { state: NodeRunState }) {
  if (state === "active") return <CircularProgress size={14} thickness={6} className="wf-node__spinner" />;
  if (state === "done") return <CheckCircleIcon fontSize="small" className="wf-node__badge wf-node__badge--done" />;
  if (state === "failed") return <ErrorIcon fontSize="small" className="wf-node__badge wf-node__badge--failed" />;
  if (state === "skipped")
    return <RemoveCircleOutlineIcon fontSize="small" className="wf-node__badge wf-node__badge--skipped" />;
  return null;
}

function conditionDescription(
  node: WorkflowNode,
  conditionFields: ConditionFieldDef[],
): { text: string; missing: boolean } | undefined {
  if (node.config.conditionMode === "field") {
    const def = conditionFields.find((f) => f.key === node.config.field);
    if (!def || !node.config.value) {
      return { text: "Please enter this value — pick a field and value to compare.", missing: true };
    }
    const opText = node.config.operator === "is_not" ? "is not" : "is";
    return { text: `If ${def.label} ${opText} "${node.config.value}"`, missing: false };
  }
  return node.config.prompt ? { text: node.config.prompt, missing: false } : undefined;
}

/** Labels of any `required` action argFields left empty (or still on their unresolved default). */
function missingRequiredFieldLabels(node: WorkflowNode, argFields?: NodeDefArgField[]): string[] {
  if (!argFields) return [];
  return argFields
    .filter((f) => f.required)
    .filter((f) => !(node.config.args?.[f.key] ?? f.defaultTemplate ?? "").trim())
    .map((f) => f.label);
}

export default function NodeCard({
  node,
  label,
  description,
  argFields,
  conditionFields,
  runState,
  selected,
  isInputHighlighted,
  onHeaderMouseDown,
  onSelect,
  onOpenPanel,
  onPortMouseDown,
  onRename,
  onDuplicate,
  onResizeMouseDown,
  registerRef,
}: NodeCardProps) {
  const isCondition = node.type === "condition";
  const conditionOptions = isCondition ? getConditionOptions(node.config) : [];
  const conditionDesc = isCondition ? conditionDescription(node, conditionFields) : undefined;
  const missingFields = node.type === "action" ? missingRequiredFieldLabels(node, argFields) : [];
  const currentLabel = node.config.label || label;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(currentLabel);

  const commitRename = () => {
    setIsEditing(false);
    const trimmed = draftLabel.trim();
    if (trimmed && trimmed !== currentLabel) onRename(trimmed);
    else setDraftLabel(currentLabel);
  };

  return (
    <div
      ref={registerRef}
      className={[
        "wf-node",
        CATEGORY_CLASS[node.type],
        `wf-node--${runState}`,
        selected ? "wf-node--selected" : "",
      ].join(" ")}
      style={{
        left: node.x,
        top: node.y,
        width: node.config.width,
        height: node.config.height,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onOpenPanel(e);
      }}
      onDoubleClick={onSelect}
      data-node-id={node.id}
    >
      <div className="wf-node__header" onMouseDown={isEditing ? undefined : onHeaderMouseDown}>
        <span className="wf-node__icon">
          <CategoryIcon type={node.type} />
        </span>
        {isEditing ? (
          <input
            className="wf-node__title-input"
            autoFocus
            value={draftLabel}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => setDraftLabel(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") {
                setDraftLabel(currentLabel);
                setIsEditing(false);
              }
            }}
          />
        ) : (
          <span className="wf-node__title">{currentLabel}</span>
        )}
        <RunBadge state={runState} />
        <IconButton
          size="small"
          className="wf-node__menu-btn"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="inherit" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setDraftLabel(currentLabel);
              setIsEditing(true);
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onDuplicate();
            }}
          >
            Duplicate
          </MenuItem>
        </Menu>
      </div>
      {description && !isCondition && <div className="wf-node__desc">{description}</div>}
      {isCondition && conditionDesc && (
        <div className={`wf-node__desc ${conditionDesc.missing ? "wf-node__desc--missing" : "wf-node__desc--prompt"}`}>
          {conditionDesc.text}
        </div>
      )}
      {missingFields.length > 0 && (
        <div className="wf-node__desc wf-node__desc--missing">
          Please enter this value — {missingFields.join(", ")}
        </div>
      )}

      {node.type !== "trigger" && (
        <div
          className={`wf-port wf-port--in ${isInputHighlighted ? "wf-port--highlight" : ""}`}
          data-port="in"
          data-node-id={node.id}
        />
      )}

      {isCondition ? (
        <>
          {conditionOptions.map((option, index) => {
            const suffix = conditionColorSuffix(index);
            const top = `${((index + 1) / (conditionOptions.length + 1)) * 100}%`;
            return (
              <div
                key={option.id}
                className={`wf-port wf-port--condition wf-port--${suffix}`}
                style={{ top }}
                data-port={option.id}
                data-node-id={node.id}
                onMouseDown={(e) => onPortMouseDown(option.id, e)}
              >
                <span className={`wf-port__label wf-port__label--${suffix}`} title={option.label}>{option.label}</span>
              </div>
            );
          })}
        </>
      ) : (
        <div
          className="wf-port wf-port--out"
          data-port="out"
          data-node-id={node.id}
          onMouseDown={(e) => onPortMouseDown("out", e)}
        />
      )}

      {RESIZE_DIRECTIONS.map((direction) => (
        <div
          key={direction}
          className={`wf-node__resize-handle wf-node__resize-handle--${direction}`}
          onMouseDown={(e) => {
            e.stopPropagation();
            onResizeMouseDown(direction, e);
          }}
        />
      ))}
      {(["e", "w"] as const).map((direction) => (
        <React.Fragment key={direction}>
          <div
            className={`wf-node__resize-handle wf-node__resize-handle--${direction} wf-node__resize-handle--${direction}-top`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeMouseDown(direction, e);
            }}
          />
          <div
            className={`wf-node__resize-handle wf-node__resize-handle--${direction} wf-node__resize-handle--${direction}-bottom`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeMouseDown(direction, e);
            }}
          />
        </React.Fragment>
      ))}
    </div>
  );
}
